import { useTranslation } from 'react-i18next';

import type { InterlockType } from './useInterlock';

/**
 * SLICE 14e — the engine-interlock cell on the bench.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 `starter` IS AN ACCEPTED RISK, NOT A HEALTHY STATE — AND IT MUST READ AS
 * ONE.
 * ─────────────────────────────────────────────────────────────────────────────
 * A `starter` interlock switches OFF the command guard's speed and freshness
 * checks for this device. That is deliberate on starter-cut hardware (a starter
 * relay cannot stall a running engine), but it means the two guards that stop a
 * command reaching a MOVING vehicle are gone. So this state gets `raad-warn`
 * tone and copy that says the guards are off — the same shape `ClearanceCell`
 * uses for a clearance, and for the same reason: a warning worn as a green tick
 * is how "accepted risk" stops meaning anything.
 *
 *   starter        → warn tone, "guards off" copy. An accepted risk.
 *   ignition/fuel  → neutral tone. The full guard applies; a cut CAN stall a
 *                    running engine, so the guard must, and does.
 *   null (not      → neutral, muted. The copy says the FULL guard applies — this
 *   recorded)        is the safe default, NOT a missing setting or an error.
 *                    NULL gets the full guard by design.
 *
 * Presentational only: it renders the `interlockType` it is handed and calls
 * `onEdit`. It reads no store and derives no state, so a test can hand it each
 * value and prove the words track the field. It assumes its HOST has gated it to
 * a super-admin (as the bench gates `ClearanceCell`); it carries no role check
 * of its own, exactly as `ClearanceCell` carries none.
 */
export function InterlockCell({
  interlockType,
  onEdit,
}: {
  /** The device's current interlock, or `null` for "not recorded". */
  interlockType: InterlockType | null;
  onEdit: () => void;
}) {
  const { t } = useTranslation();

  const editButton = (label: string) => (
    <button
      type="button"
      onClick={onEdit}
      className="mt-1 block min-h-8 rounded-lg border border-raad-border px-2.5 text-[12px] font-semibold transition-colors hover:bg-raad-primary/10"
    >
      {label}
    </button>
  );

  if (interlockType === 'starter') {
    // ACCEPTED RISK. Warn tone, and the copy names the consequence.
    return (
      <span className="block">
        <span className="font-semibold text-raad-warn">{t('interlock.state.starter')}</span>
        <span className="mt-0.5 block text-[11px] text-raad-muted">
          {t('interlock.state.starterHint')}
        </span>
        {editButton(t('interlock.action.change'))}
      </span>
    );
  }

  if (interlockType === 'ignition' || interlockType === 'fuel') {
    // Full guard applies — neutral, not a warning.
    return (
      <span className="block">
        <span className="font-semibold text-raad-text">
          {t(interlockType === 'ignition' ? 'interlock.state.ignition' : 'interlock.state.fuel')}
        </span>
        <span className="mt-0.5 block text-[11px] text-raad-muted">
          {t('interlock.state.guardHint')}
        </span>
        {editButton(t('interlock.action.change'))}
      </span>
    );
  }

  // NOT RECORDED (null). The safe default — full guard — never an error.
  return (
    <span className="block">
      <span className="text-raad-muted">{t('interlock.state.notRecorded')}</span>
      <span className="mt-0.5 block text-[11px] text-raad-muted">
        {t('interlock.state.notRecordedHint')}
      </span>
      {editButton(t('interlock.action.set'))}
    </span>
  );
}
